import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2, Eye, EyeOff, AlertCircle, Search, Clock, CheckCircle, FileEdit } from 'lucide-react';
import MyLayout from './Layout';
import { apiFetchJson } from './api';

const CATEGORIES = ['serveurs', 'cyber', 'ia', 'telephonie', 'impression', 'formation'];
const CAT_LABELS = {
  serveurs: 'Serveurs', cyber: 'Cybersécurité', ia: 'IA',
  telephonie: 'Téléphonie', impression: 'Impression', formation: 'Formation',
};

const EMPTY = {
  title_fr: '', title_en: '', excerpt_fr: '', excerpt_en: '',
  content_fr: '', content_en: '', category: 'serveurs', slug: '',
  image_url: '', published: true, publish_date: '',
};

function generateSlug(title) {
  return title.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function getArticleStatus(article) {
  const now = new Date().toISOString();
  if (!article.published) return 'draft';
  if (article.publish_date && article.publish_date > now) return 'scheduled';
  return 'published';
}

function StatusBadge({ article }) {
  const status = getArticleStatus(article);
  if (status === 'published') return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">
      <CheckCircle className="w-2.5 h-2.5" /> Publié
    </span>
  );
  if (status === 'scheduled') return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">
      <Clock className="w-2.5 h-2.5" /> Programmé
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-400">
      <FileEdit className="w-2.5 h-2.5" /> Brouillon
    </span>
  );
}

export default function MyNews() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeLang, setActiveLang] = useState('fr');
  const [search, setSearch] = useState('');

  const fetchArticles = async () => {
    setLoading(true);
    try {
      setArticles(await apiFetchJson('/api/admin/news', {}, 'Erreur de chargement'));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchArticles(); }, []);

  const filteredArticles = useMemo(() => {
    if (!search.trim()) return articles;
    const q = search.toLowerCase();
    return articles.filter(a =>
      (a.title_fr || '').toLowerCase().includes(q) ||
      (a.title_en || '').toLowerCase().includes(q)
    );
  }, [articles, search]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'title_fr' && !form.slug) {
      setForm(f => ({ ...f, title_fr: value, slug: generateSlug(value) }));
    }
  };

  const startEdit = article => {
    setEditing(article.id);
    setForm({
      title_fr: article.title_fr || '', title_en: article.title_en || '',
      excerpt_fr: article.excerpt_fr || '', excerpt_en: article.excerpt_en || '',
      content_fr: article.content_fr || '', content_en: article.content_en || '',
      category: article.category || 'serveurs', slug: article.slug || '',
      image_url: article.image_url || '', published: article.published ?? true,
      publish_date: article.publish_date ? article.publish_date.slice(0, 16) : '',
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startCreate = () => {
    setEditing('new');
    setForm(EMPTY);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!form.title_fr.trim()) { setError('Le titre FR est obligatoire'); return; }
    setSaving(true);
    setError('');
    const data = {
      ...form,
      slug: form.slug || generateSlug(form.title_fr),
      publish_date: form.publish_date ? new Date(form.publish_date).toISOString() : null,
    };
    try {
      await (editing === 'new'
        ? apiFetchJson('/api/admin/news', { method: 'POST', body: JSON.stringify(data) }, 'Erreur de création')
        : apiFetchJson(`/api/admin/news/${editing}`, { method: 'PUT', body: JSON.stringify(data) }, 'Erreur de mise à jour'));
      setEditing(null);
      setForm(EMPTY);
      await fetchArticles();
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    }
    setSaving(false);
  };

  const handleDelete = async id => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await apiFetchJson(`/api/admin/news/${id}`, { method: 'DELETE' }, 'Erreur de suppression');
      await fetchArticles();
    } catch {}
  };

  const handleTogglePublish = async article => {
    try {
      await apiFetchJson(`/api/admin/news/${article.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...article, published: !article.published }),
      }, 'Erreur de mise à jour');
      await fetchArticles();
    } catch {}
  };

  const cancelEdit = () => { setEditing(null); setForm(EMPTY); setError(''); };

  return (
    <MyLayout title="Actualités">
      <div className="space-y-6">
        {/* Barre du haut : recherche + créer */}
        {!editing && (
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un article..."
                className="w-full bg-[#12141a] border border-white/5 focus:border-[#0099D6]/50 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 outline-none transition-colors"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={startCreate}
              className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-2.5 px-5 rounded-xl transition-colors text-sm shrink-0"
            >
              <Plus className="w-4 h-4" /> Nouvel article
            </button>
          </div>
        )}

        {/* Éditeur */}
        {editing && (
          <div className="bg-[#12141a] border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold">
                {editing === 'new' ? 'Nouvel article' : "Modifier l'article"}
              </h3>
              <div className="flex gap-1 bg-black/20 rounded-xl p-1">
                <button
                  onClick={() => setActiveLang('fr')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${activeLang === 'fr' ? 'bg-[#0099D6] text-white' : 'text-gray-400 hover:text-white'}`}
                >FR</button>
                <button
                  onClick={() => setActiveLang('en')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${activeLang === 'en' ? 'bg-[#0099D6] text-white' : 'text-gray-400 hover:text-white'}`}
                >EN</button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400 mb-4">
                <AlertCircle className="w-4 h-4 shrink-0" /> {error}
              </div>
            )}

            <div className="space-y-4">
              <Field label={`Titre ${activeLang === 'fr' ? 'FR' : 'EN'}`} name={`title_${activeLang}`} value={form[`title_${activeLang}`]} onChange={handleChange} />
              <Field label={`Chapeau ${activeLang === 'fr' ? 'FR' : 'EN'}`} name={`excerpt_${activeLang}`} value={form[`excerpt_${activeLang}`]} onChange={handleChange} />
              <TextArea label={`Contenu ${activeLang === 'fr' ? 'FR' : 'EN'}`} name={`content_${activeLang}`} value={form[`content_${activeLang}`]} onChange={handleChange} />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Catégorie</label>
                  <select
                    name="category" value={form.category} onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white text-sm outline-none"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                  </select>
                </div>
                <Field label="Slug (URL)" name="slug" value={form.slug} onChange={handleChange} placeholder="auto" />
                <Field label="URL Image" name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." />
              </div>

              {/* Statut et date de publication */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-black/20 rounded-xl border border-white/5">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Statut</label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
                    <input
                      type="checkbox" name="published" checked={form.published} onChange={handleChange}
                      className="w-4 h-4 rounded accent-[#0099D6]"
                    />
                    Publié
                  </label>
                  <p className="text-[10px] text-gray-600 mt-1">
                    {!form.published ? 'Brouillon — non visible sur le site' :
                      form.publish_date && new Date(form.publish_date) > new Date() ? 'Programmé — visible à la date choisie' :
                      'Publié immédiatement'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">
                    Date de publication <span className="text-gray-600">(optionnelle)</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="publish_date"
                    value={form.publish_date}
                    onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white text-sm outline-none"
                  />
                  <p className="text-[10px] text-gray-600 mt-1">
                    Si vide et publié : visible immédiatement
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-2.5 px-5 rounded-xl transition-colors text-sm disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
              <button
                onClick={cancelEdit}
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-gray-300 font-medium py-2.5 px-5 rounded-xl transition-colors text-sm"
              >
                <X className="w-4 h-4" /> Annuler
              </button>
            </div>
          </div>
        )}

        {/* Info résultats recherche */}
        {search && !editing && (
          <p className="text-xs text-gray-500">
            {filteredArticles.length} résultat{filteredArticles.length !== 1 ? 's' : ''} pour « {search} »
          </p>
        )}

        {/* Liste des articles */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 text-[#0099D6] animate-spin" />
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="bg-[#12141a] border border-white/5 rounded-2xl p-12 text-center">
            <p className="text-gray-500 text-sm">
              {search ? 'Aucun article ne correspond à votre recherche.' : 'Aucun article. Créez le premier !'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredArticles.map(article => (
              <div
                key={article.id}
                className={`bg-[#12141a] border rounded-xl px-5 py-4 flex items-center gap-4 transition-colors ${
                  editing === article.id ? 'border-[#0099D6]/30' : 'border-white/5 hover:border-white/10'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <StatusBadge article={article} />
                    <span className="text-[10px] text-gray-600 uppercase tracking-wider">{CAT_LABELS[article.category] || article.category}</span>
                    {article.publish_date && (
                      <span className="text-[10px] text-gray-600">
                        · {new Date(article.publish_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-medium text-white truncate">{article.title_fr}</h4>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleTogglePublish(article)}
                    className={`p-2 rounded-lg transition-colors ${article.published ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-gray-600 hover:bg-white/5 hover:text-gray-400'}`}
                    title={article.published ? 'Dépublier' : 'Publier'}
                  >
                    {article.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => startEdit(article)}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 transition-colors"
                    title="Modifier"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(article.id)}
                    className="p-2 rounded-lg hover:bg-red-500/10 text-gray-600 hover:text-red-400 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MyLayout>
  );
}

function Field({ label, name, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>
      <input
        type="text" name={name} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white placeholder-gray-600 outline-none text-sm"
      />
    </div>
  );
}

function TextArea({ label, name, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>
      <textarea
        name={name} value={value} onChange={onChange} rows={6}
        className="w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-xl p-2.5 text-white placeholder-gray-600 outline-none resize-none text-sm"
      />
    </div>
  );
}
